import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { defaultSeo, routeSeo, siteConfig } from "../seoData";

const stripHtml = (value = "") => value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const decodeHtml = (value = "") => {
  if (typeof document === "undefined") {
    return stripHtml(value);
  }

  const textarea = document.createElement("textarea");
  textarea.innerHTML = stripHtml(value);
  return textarea.value;
};

const absoluteUrl = (path = "/") => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${siteConfig.siteUrl}${cleanPath}`;
};

const canonicalPath = (pathname = "/") => {
  if (pathname === "/") {
    return "/";
  }

  return pathname.replace(/\/+$/, "");
};

const setMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

const setLink = (rel, href) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
};

const getPathSlug = (pathname) => {
  if (pathname === "/") {
    return "home";
  }

  return pathname.split("/").filter(Boolean).pop();
};

const getWordPressImage = (wpSeo = {}) =>
  wpSeo.og_image?.[0]?.url || wpSeo.twitter_image || wpSeo.schema?.["@graph"]?.find((item) => item?.url)?.url;

const removeBackendPath = (url) => {
  if (!url) {
    return url;
  }

  return url.replace("/bkadmn", "");
};

function SEO({
  title,
  description,
  keywords,
  image,
  type = "website",
  canonicalPathOverride,
  wpSeo,
  useWordPressSeo = true,
}) {
  const location = useLocation();
  const pathname = canonicalPathOverride || canonicalPath(location.pathname);
  const pageSeo = routeSeo[pathname] || defaultSeo;
  const [fetchedWpSeo, setFetchedWpSeo] = useState(null);
  const activeWpSeo = wpSeo || fetchedWpSeo || {};
  const wpSlug = pageSeo.wpSlug || getPathSlug(pathname);
  const explicitSeoProvided = Boolean(title || description || image || wpSeo);

  useEffect(() => {
    if (!useWordPressSeo || explicitSeoProvided || !wpSlug) {
      setFetchedWpSeo(null);
      return undefined;
    }

    const controller = new AbortController();
    const pageUrl = `${siteConfig.wordpressApiUrl}/pages?slug=${encodeURIComponent(wpSlug)}&_fields=yoast_head_json,title,excerpt`;

    fetch(pageUrl, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : []))
      .then((pages) => {
        if (!controller.signal.aborted) {
          setFetchedWpSeo(pages?.[0]?.yoast_head_json || null);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setFetchedWpSeo(null);
        }
      });

    return () => controller.abort();
  }, [explicitSeoProvided, useWordPressSeo, wpSlug]);

  const wpCanonical = removeBackendPath(activeWpSeo.canonical);
  const metaTitle = decodeHtml(title || activeWpSeo.title || activeWpSeo.og_title || pageSeo.title || defaultSeo.title);
  const metaDescription = decodeHtml(
    description || activeWpSeo.description || activeWpSeo.og_description || pageSeo.description || defaultSeo.description
  );
  const metaKeywords = decodeHtml(keywords || pageSeo.keywords || defaultSeo.keywords);
  const canonical = wpCanonical || absoluteUrl(pathname);
  const metaImage = absoluteUrl(image || getWordPressImage(activeWpSeo) || pageSeo.image || siteConfig.defaultImage);
  const metaType = activeWpSeo.og_type || type || pageSeo.type || defaultSeo.type;
  const robots = useMemo(() => {
    if (activeWpSeo.robots && typeof activeWpSeo.robots === "object") {
      return Object.values(activeWpSeo.robots).filter(Boolean).join(", ");
    }

    return "index, follow";
  }, [activeWpSeo.robots]);

  useEffect(() => {
    document.title = metaTitle;

    setMeta('meta[name="description"]', { name: "description", content: metaDescription });
    setMeta('meta[name="keywords"]', { name: "keywords", content: metaKeywords });
    setMeta('meta[name="robots"]', { name: "robots", content: robots });
    setMeta('meta[name="author"]', { name: "author", content: siteConfig.siteName });

    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: siteConfig.siteName });
    setMeta('meta[property="og:type"]', { property: "og:type", content: metaType });
    setMeta('meta[property="og:title"]', { property: "og:title", content: metaTitle });
    setMeta('meta[property="og:description"]', { property: "og:description", content: metaDescription });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setMeta('meta[property="og:image"]', { property: "og:image", content: metaImage });

    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: metaTitle });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: metaDescription });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: metaImage });

    if (siteConfig.twitterHandle) {
      setMeta('meta[name="twitter:site"]', { name: "twitter:site", content: siteConfig.twitterHandle });
    }

    setLink("canonical", canonical);
  }, [canonical, metaDescription, metaImage, metaKeywords, metaTitle, metaType, robots]);

  return null;
}

export default SEO;
